import { Session } from 'next-auth';

import {
    getProductSpecifications,
    getProductsContent,
    getProductsHighlights,
    getProductsSpecialOffers,
} from '@services/cms/products';
import { httpService } from '@services/http';
import { HttpConfig, HttpService } from '@services/http/http';
import { personServices } from '@services/person';
import { getSimulateDataFromApi } from '@services/renewal';
import { reviewsServices } from '@services/reviews';

import { getServerSession } from '@actions/get-session';

import { ContractType } from '@app-types/contract';
import { MobilePlanType } from '@app-types/plan';
import { ProductParentFull } from '@app-types/product';
import { GalliumDataStoreApi } from '@app-types/renewal';
import { UserType, UserTypeEnum } from '@app-types/user';

import { isServer } from '@helpers/config';
import { localApiPaths } from '@helpers/path';
import { addRenewalInfoToPlans } from '@helpers/product';

import { PlanTypeId } from '@constants/plan';

import { sapiProductMapper } from '@product-detail/helpers';

import { getApiProductDetails, mergeSapicToSapiProduct } from '../products';
import { getMobilePlans, getPlanTypeFromUrlKey } from './plan';

class ProductDetailsService {
    private httpService: HttpService;

    public constructor(httpService: HttpService) {
        this.httpService = httpService;
    }

    public async getProductDetails(
        name: string,
        isRenewal: boolean = false,
        shouldBeProspect: boolean = false,
        _session?: Session,
    ): Promise<ProductParentFull> {
        const session = shouldBeProspect ? null : (_session ?? (await getServerSession()));
        if (isServer()) {
            return this.getProductDetailsServer(name, isRenewal, session);
        }

        const config: HttpConfig = {};

        if (session?.user) {
            config.user = session.user as UserType;
        }

        return this.httpService.get<ProductParentFull>(
            localApiPaths.product,
            {
                name,
                isRenewal,
                shouldBeProspect,
            },
            config,
        );
    }

    public async getPlans(
        productGencode: string,
        isRenewal: boolean,
        planUrlKey?: string | null,
        contract?: ContractType,
        session?: Session,
        currentGalliumData?: GalliumDataStoreApi,
    ): Promise<{
        plans: MobilePlanType[];
        galliumData?: GalliumDataStoreApi;
    }> {
        let plans: MobilePlanType[] = [];
        let galliumData = currentGalliumData;

        if (isRenewal && contract?.id && session?.user) {
            galliumData ??= await getSimulateDataFromApi(contract.id, session.user as UserType);

            if (!galliumData) {
                return { plans: [], galliumData: undefined };
            }

            const plansSimulation = galliumData.simulation.map((offer) => offer.idOffre);
            const plansGencode = [galliumData.opSource.idOffre, ...plansSimulation].filter((o) => o !== undefined);

            plans = await getMobilePlans(
                productGencode,
                true,
                PlanTypeId.PREMIUM,
                contract?.id,
                plansGencode,
                session,
                true,
                false,
            );

            plans = addRenewalInfoToPlans(plans, galliumData);
        } else {
            const urlKeyType = getPlanTypeFromUrlKey(planUrlKey);

            plans = await getMobilePlans(
                productGencode,
                isRenewal,
                urlKeyType,
                contract?.id,
                isRenewal && contract ? [contract.subscription.offerId] : undefined,
                isRenewal ? session : undefined,
                true,
                false,
            );
        }

        plans = plans.filter((p) => p.simulatedCart?.fundings.length);

        if (plans.length === 0) {
            return { plans: [], galliumData };
        }

        return {
            plans,
            galliumData,
        };
    }

    private async getProductDetailsServer(
        name: string,
        isRenewal: boolean,
        session: Session | null,
    ): Promise<ProductParentFull> {
        const user = session?.user as UserType | undefined;

        const [productSapi, contact] = await Promise.all([
            getApiProductDetails(name),
            user && user.user_type !== UserTypeEnum.CDC ? personServices.getContactDetails(user.sub, user) : undefined,
        ]);

        const [product, specifications, highlights, specialOffers, content] = await Promise.all([
            mergeSapicToSapiProduct(productSapi, session),
            getProductSpecifications(productSapi.url_key),
            getProductsHighlights(productSapi.url_key),
            getProductsSpecialOffers(productSapi.url_key, isRenewal),
            getProductsContent(productSapi.url_key),
        ]);

        const [productInfo, reviews] = await Promise.all([
            sapiProductMapper(product, contact?.address.zipCode ?? '75000'),
            reviewsServices.getReviews(productSapi.gencode),
        ]);

        return {
            ...productInfo,
            reviews,
            specificationsCms: specifications,
            highlightsCms: highlights,
            specialOffersCms: specialOffers,
            productContentCms: content,
        };
    }
}

export const productDetailsService = new ProductDetailsService(httpService);
